import { Days, Status, ExtraChargesType } from '@/core/types/utils'
import { z } from 'zod'
import { PricingType } from '../../rooms/rooms.types'
import { addOnsSchema } from '../../rooms/add-ons/addOns.types'

export enum AvailableType {
  'ALWAYS' = 'ALWAYS',
  'SPECIFIC RANGE' = 'SPECIFIC RANGE',
}

export enum AccessRate {
  'promotional rate' = 'promotional rate',
  'no restrictions' = 'no restrictions',
  'member rate' = 'member rate',
}

export enum LOSDiscountsType {
  'percentageOff' = 'percentageOff',
  'nightFree' = 'nightFree',
}

export enum LOSDiscountsStay {
  '1 Night' = 1,
  '2 Nights' = 2,
  '3 Nights' = 3,
  '4 Nights' = 4,
  '5 Nights' = 5,
  '6 Nights' = 6,
  '7 Nights' = 7,
  '8 Nights' = 8,
  '9 Nights' = 9,
  '10 Nights' = 10,
  '11 Nights' = 11,
  '12 Nights' = 12,
  '13 Nights' = 13,
  '14 Nights' = 14,
  '15 Nights' = 15,
  '21 Nights' = 21,
  '28 Nights' = 28,
}

export enum Deposits {
  'Take no deposit' = 'Take no deposit',
  'Take entire amount' = 'Take entire amount',
  'Take a fixed amount' = 'Take a fixed amount',
  'Take a percentage' = 'Take a percentage',
  'Take value of first night' = 'Take value of first night',
}

export enum DiscountType {
  'fixedPrice' = 'fixedPrice',
  'percentage' = 'percentage',
}

export const RATE_FORM_TABS = {
  rateDetails: 'generalInfo',
  mediaDetails: 'generalInfo',
  customMessages: 'generalInfo',
  specificDaysRate: 'roomsAndPricing',
  cancellationPolicies: 'roomsAndPricing',
  taxCancellationPolicies: 'roomsAndPricing',
  limitations: 'limitations',
  addons: 'addonsAndOptions',
}

export const RATE_LINK_FORM_TABS = {
  rateLinks: 'generalInfo',
  mediaDetails: 'generalInfo',
  customMessages: 'generalInfo',
  rateDetails: 'generalInfo',
  limitations: 'limitations',
  addons: 'addonsAndOptions',
  specificDaysRate: 'roomsAndPricing',
}

export const PACKAGE_FORM_TABS = {
  packageDetails: 'generalInfo',
  limitations: 'limitations',
  roomsAndPricing: 'roomsAndPricing',
  specificDaysRate: 'roomsAndPricing',
  pricingActivity: 'roomsAndPricing',
}

export const losDiscountsFormSchema = z.object({
  nights: z.enum(LOSDiscountsStay),
  type: z.enum(LOSDiscountsType),
  value: z.transform(Number).pipe(z.number().min(1).max(100)),
})
export type LosDiscountsFormValues = z.infer<typeof losDiscountsFormSchema>

export const customMessagesFormSchema = z.object({
  titleCustomMessagesRate: z.string().max(30),
  descriptionCustomMessagesRate: z.string().max(90),
  orderCustomMessagesRate: z.transform(Number).pipe(z.number().int().min(1)),
})
export type CustomMessagesFormValues = z.infer<typeof customMessagesFormSchema>

export const daysFormSchema = z.object({
  Monday: z.boolean(),
  Tuesday: z.boolean(),
  Wednesday: z.boolean(),
  Thursday: z.boolean(),
  Friday: z.boolean(),
  Saturday: z.boolean(),
  Sunday: z.boolean(),
})

const specificDaysRateFormSchema = z.object({
  roomId: z.string().optional(),
  day: z.enum(Days),
  singleDiscount: z.transform(Number).pipe(z.number()),
  perAdult: z.transform(Number).pipe(z.number()),
  perRoom: z.transform(Number).pipe(z.number()),
  perChildren: z.transform(Number).pipe(z.number()),
  allocation: z.transform(Number).pipe(z.number()),
  roomName: z.string().optional(),
  type: z.enum(DiscountType).optional(),
  pricing: z.enum(PricingType),
})

export type SpecificDaysRate = z.infer<typeof specificDaysRateFormSchema>

const rateDetailsFormSchema = z.object({
  status: z.enum(Status),
  orderIndex: z.transform(Number).pipe(z.number().int().min(0).max(1000)),
  availableType: z.enum(AvailableType).nullish(),
  availableFrom: z.string(),
  availableTo: z.string(),
  shortTitle: z.string().max(25).min(1, { error: 'val.required' }),
  longTitle: z.string().max(40).min(1, { error: 'val.required' }),
  rateDescription: z.string().max(350).min(1, { error: 'val.required' }),
  rateGroupId: z.string().optional().nullish(),
  termsAndCondition: z.string().max(500).min(1, { error: 'val.required' }),
  rateCode: z.string().max(60, { error: 'val.max60' }),
  ratesTags: z.array(z.string()),
  setAsDefaultRate: z.boolean(),
  externalRateCode: z.string().optional().nullish(),
})

export type RateDetails = z.infer<typeof rateDetailsFormSchema>

const rateLimitationsFormSchema = z.object({
  accessRate: z.enum(AccessRate),
  minimumLengthOfStay: z
    .transform(Number)
    .pipe(z.number().int().min(1, { error: 'val.min1' })),
  maximumLengthOfStay: z
    .transform(Number)
    .pipe(
      z
        .number()
        .int()
        .min(1, { error: 'val.min1' })
        .max(99, { error: 'val.max99' })
    ),
  minimumLeadIn: z
    .transform(Number)
    .pipe(
      z
        .number()
        .int()
        .min(1, { error: 'val.min1' })
        .max(365, { error: 'val.max365' })
    ),
  maximumLeadIn: z
    .transform(Number)
    .pipe(
      z
        .number()
        .int()
        .min(1, { error: 'val.min1' })
        .max(365, { error: 'val.max365' })
    ),
  checkinDays: daysFormSchema,
  losDiscounts: z.array(losDiscountsFormSchema).optional(),
  desktopDist: z.boolean(),
  mobileDist: z.boolean(),
  promoCode: z.array(z.string()).optional(),
})

const policiesFormSchema = z.object({
  taxPolicy: z.string().length(16),
  cancellationPolicy: z.string().length(16),
  deposits: z.enum(Deposits),
  value: z.transform(Number).pipe(z.number()).optional(),
})

export const extraChargesFormSchema = z.object({
  id: z.string().length(16).optional(),
  name: z.string().min(0).max(50),
  amount: z.transform(Number).pipe(z.number().min(0)),
  type: z.enum(ExtraChargesType),
})

export type ExtraChargesFormValues = z.infer<typeof extraChargesFormSchema>

export const rateFormSchema = z.object({
  rateDetails: rateDetailsFormSchema,
  mediaDetails: z.object({
    images: z.array(z.string()).min(1, { error: 'val.required' }),
  }),
  customMessages: z.array(customMessagesFormSchema).max(3),
  limitations: rateLimitationsFormSchema,
  specificDaysRate: z
    .array(specificDaysRateFormSchema)
    .min(1, { error: 'val.required' }),
  policies: policiesFormSchema,
  addons: z.array(z.string()),
  extraCharges: z.array(extraChargesFormSchema),
})

export type RateFormValues = z.infer<typeof rateFormSchema>
export type RateFormInputValues = z.input<typeof rateFormSchema>

export const rateSchema = rateFormSchema
  .omit({
    addons: true,
  })
  .extend({
    id: z.string(),
    addons: z.array(addOnsSchema),
    rateDetails: rateDetailsFormSchema.extend({
      propertyId: z.string(),
      type: z.string(),
      status: z.enum(Status),
    }),
  })

export type Rate = z.infer<typeof rateSchema>

export const pricingActivitySchema = z.object({
  activityName: z.string().optional(),
  activityId: z.string(),
  discount: z.transform(Number).pipe(z.number()),
  Monday: z.transform(Number).pipe(z.number()),
  Tuesday: z.transform(Number).pipe(z.number()),
  Wednesday: z.transform(Number).pipe(z.number()),
  Thursday: z.transform(Number).pipe(z.number()),
  Friday: z.transform(Number).pipe(z.number()),
  Saturday: z.transform(Number).pipe(z.number()),
  Sunday: z.transform(Number).pipe(z.number()),
  type: z.enum(DiscountType).optional(),
})

export type PricingActivity = z.infer<typeof pricingActivitySchema>

export const linkedRateFormSchema = rateFormSchema.extend({
  rateDetails: rateDetailsFormSchema.extend({
    externalRateCode: z.string().min(1, { error: 'val.required' }),
  }),
})

export type LinkedRateFormValues = z.infer<typeof linkedRateFormSchema>
export const packageFormSchema = rateFormSchema
  .extend({
    id: z.string().optional(),
    packageDetails: rateDetailsFormSchema.omit({
      setAsDefaultRate: true,
      externalRateCode: true,
    }),
    pricingActivity: z
      .array(
        pricingActivitySchema.omit({
          Friday: true,
          Saturday: true,
          Sunday: true,
          Monday: true,
          Tuesday: true,
          Wednesday: true,
          Thursday: true,
        })
      )
      .min(1, { error: 'val.required' }),
  })
  .omit({
    rateDetails: true,
    extraCharges: true,
  })

export const packageSchema = rateFormSchema
  .omit({
    addons: true,
  })
  .extend({
    id: z.string().optional(),
    packageDetails: rateDetailsFormSchema.omit({
      setAsDefaultRate: true,
      externalRateCode: true,
    }),
    pricingActivity: z
      .array(
        pricingActivitySchema.omit({
          Friday: true,
          Saturday: true,
          Sunday: true,
          Monday: true,
          Tuesday: true,
          Wednesday: true,
          Thursday: true,
        })
      )
      .min(1, { error: 'val.required' }),
    addons: z.array(addOnsSchema),
  })
  .omit({
    rateDetails: true,
    extraCharges: true,
  })

export type PackageFormValues = z.infer<typeof packageFormSchema>
export type PackageFormInputValues = z.input<typeof packageFormSchema>
export type Package = z.infer<typeof packageSchema>

export const RateGroupsFormSchema = z.object({
  name: z.string().max(255, { error: 'val.max255' }),
  description: z.string().max(500, { error: 'val.max255' }),
  status: z.enum(Status).optional(),
  externalId: z.string().optional().nullish(),
  rateGroupCode: z.string().max(10, { error: 'val.max10' }),
})

export const RateGroup = RateGroupsFormSchema.extend({
  id: z.string(),
  propertyId: z.string(),
  rates: z
    .array(
      z.object({
        id: z.string(),
        shortTitle: z.string(),
        rateCode: z.string(),
      })
    )
    .optional()
    .nullish(),
})

export type RateGroupsFormValues = z.infer<typeof RateGroupsFormSchema>
export type RateGroupsFormInputValues = z.input<typeof RateGroupsFormSchema>
export type RateGroups = z.infer<typeof RateGroup>
